import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { contactFormApi } from "api/index";
import { AxiosError } from "axios";
import cn from "classnames";
import { FlexibleFields } from "components/DynamicFields/FlexibleFields";
import { Button } from "components/UI/Button";
import { FormFieldControlled } from "components/UI/FormField";
import { Link } from "components/UI/Link";
import { Loader } from "components/UI/Loader/Loader";
import { PhoneSelect } from "components/UI/PhoneSelect";
import { Select, SelectItem } from "components/UI/Select";
import { useStatusScreenContext } from "components/UI/StatusScreen";
import { COUNTRIES_LIST } from "constants/countries.constans";
import { resetAllDynamicFormFields } from "constants/dynamicFormFields";
import { INPUT_FIELD_NAMES, TOOL_INFO_FIELDS } from "constants/fieldNames.constans";
import { PAGE_NAME } from "constants/pageName.constans";
import { REQUEST_TYPE_OPTIONS, TOOL_ICONS, TOOL_NAMES_CONTENT } from "constants/selects.content";
import { VALIDATION_SCHEMA } from "constants/validationSchema.constans";
import { usePageName } from "context/PageName.context";
import { Country } from "types/country.types";
import type { FormValues } from "types/form.types";
import { SelectOption } from "types/select.types";
import { getFilledFormValues } from "utils/getFilledFormValues";
import { QueryParamsManager } from "utils/queryParams.lib";
import { getRecaptchaToken } from "utils/recaptcha";
import { toSelectValue } from "utils/select.utils";
import { isRequestTypeName, isToolName } from "utils/selectGuards";
import { getStoredUser, stripDialCodeFromPhone } from "utils/user.utils";
import styles from "./ContactForm.module.scss";

type PossibleUrlQueryParams = {
  serviceTool: string;
  serviceType: string;
};

type ContactFormProps = {
  isCountryLoading: boolean;
  userCountry: Country;
};

export const ContactForm = ({ isCountryLoading, userCountry }: ContactFormProps) => {
  const { pageName } = usePageName();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { setStatusResponse, setIsStatusScreenOpen } = useStatusScreenContext();
  const [toolIcon, setToolIcon] = useState<JSX.Element>();
  const [requestIcon] = useState<JSX.Element>();
  const [isLoading, setIsLoading] = useState(false);
  const { control, handleSubmit, reset, unregister, setValue } = useFormContext<FormValues>();
  const qpm = useMemo(
    () =>
      new QueryParamsManager<PossibleUrlQueryParams, keyof PossibleUrlQueryParams>(window.location.href, [
        "serviceTool",
        "serviceType",
      ]),
    []
  );

  const syncQueryParam = (key: keyof PossibleUrlQueryParams, value: string) => {
    qpm.set(key, value);
    qpm.update();
  };

  const onSubmit = async (data: FormValues) => {
    const payload = getFilledFormValues(data);
    setIsLoading(true);
    setErrorMessage("");

    let recaptcha: string;
    try {
      recaptcha = await getRecaptchaToken("CONTACT");
    } catch {
      setIsLoading(false);
      setErrorMessage("Error with reCAPTCHA verification.");
      return;
    }

    await contactFormApi
      .sendUserForm({ ...payload, recaptcha })
      .then((response) => {
        setIsLoading(false);

        if (response.status === 200) {
          setStatusResponse("success");
          setIsStatusScreenOpen(true);
          reset();
        }
      })
      .catch((error: AxiosError<{ message?: string; errors?: { recaptcha?: string[] } }>) => {
        setIsLoading(false);

        const status = error.response?.status;
        const errors = error.response?.data?.errors;

        if (status === 422 && errors?.recaptcha) {
          setErrorMessage(errors.recaptcha[0] ?? "reCAPTCHA verification failed.");
          return;
        }

        if (status === 500) {
          setStatusResponse("error");
          setIsStatusScreenOpen(true);
          return;
        }

        setStatusResponse("error");
        setIsStatusScreenOpen(true);
      });
  };

  const onToolSelectChange = (onChange: (value: FormValues["service_tool"]) => void) => (selected: SelectOption) => {
    resetAllDynamicFormFields(unregister);
    if (!isToolName(selected.name)) {
      return;
    } else {
      onChange(selected.name);
      syncQueryParam("serviceTool", selected.name);
      setToolIcon(TOOL_ICONS[selected.name]);
    }
  };

  const onRequestSelectChange = (onChange: (value: FormValues["service_type"]) => void) => (selected: SelectOption) => {
    resetAllDynamicFormFields(unregister);
    if (!isRequestTypeName(selected.name)) {
      return;
    } else {
      onChange(selected.name);
      syncQueryParam("serviceType", selected.name);
    }
  };

  useEffect(() => {
    const toolFromUrl = qpm.get("serviceTool");
    const serviceTypeFromUrl = qpm.get("serviceType");

    if (toolFromUrl && isToolName(toolFromUrl)) {
      setValue(TOOL_INFO_FIELDS.serviceTool, toolFromUrl);
      setToolIcon(TOOL_ICONS[toolFromUrl]);
    }

    if (serviceTypeFromUrl && isRequestTypeName(serviceTypeFromUrl)) {
      setValue(TOOL_INFO_FIELDS.serviceType, serviceTypeFromUrl);
    }
  }, [qpm, setValue]);

  useEffect(() => {
    const parsedUser = getStoredUser();
    if (!parsedUser?.data) {
      return;
    }

    const { data } = parsedUser;

    if (data.first_name) {
      setValue(INPUT_FIELD_NAMES.firstName, data.first_name);
    }
    if (data.last_name) {
      setValue(INPUT_FIELD_NAMES.lastName, data.last_name);
    }
    if (data.email) {
      setValue(INPUT_FIELD_NAMES.email, data.email);
    }
    if (data.company?.name) {
      setValue(INPUT_FIELD_NAMES.companyName, data.company.name);
    }
  }, [setValue]);

  useEffect(() => {
    if (isCountryLoading) {
      return;
    }

    const parsedUser = getStoredUser();
    if (!parsedUser?.data) {
      return;
    }

    const { data } = parsedUser;
    const matchedCountry = data.country_code
      ? COUNTRIES_LIST.find((country) => country.countryCode.toUpperCase() === data.country_code!.toUpperCase())
      : userCountry;

    if (!matchedCountry?.iso) {
      return;
    }

    setValue(INPUT_FIELD_NAMES.phoneDialCode, { name: matchedCountry.name, value: matchedCountry.iso });

    if (data.phone) {
      setValue(INPUT_FIELD_NAMES.phoneNumber, stripDialCodeFromPhone(data.phone, matchedCountry.iso));
    }
  }, [isCountryLoading, userCountry, setValue]);

  return (
    <div className={styles.contactForm}>
      <h1 className={styles.contactFormTitle}>
        {pageName === PAGE_NAME.REQUEST_QUOTE ? "Request a quote" : "Contact Us"}
      </h1>
      <p className={styles.contactFormSubtitle}>Let's Build Smarter Logistics Together</p>
      <form onSubmit={handleSubmit(onSubmit)}>
        {pageName === PAGE_NAME.REQUEST_QUOTE && (
          <>
            <div className={styles.wrapperSelects}>
              <div className={styles.select}>
                <Controller<FormValues, typeof TOOL_INFO_FIELDS.serviceTool>
                  rules={VALIDATION_SCHEMA.toolInfo.tool}
                  control={control}
                  name={TOOL_INFO_FIELDS.serviceTool}
                  render={({ field: { name, onChange, value }, fieldState: { error } }) => (
                    <Select
                      selectValue={toSelectValue(value, TOOL_NAMES_CONTENT)}
                      closeAfterSelection
                      name={name}
                      placeholder="Choose tools"
                      label="Tools *"
                      renderIcon={toolIcon}
                      onChange={onToolSelectChange(onChange)}
                      error={!!error}
                      errorMessage={error?.message}
                    >
                      {TOOL_NAMES_CONTENT.map(({ name, value, icon }) => {
                        return (
                          <SelectItem key={name} name={name} value={value} className={styles.toolSelectItem}>
                            <div className={styles.toolSelectItemIcon}>{icon}</div>
                            <div>{value}</div>
                          </SelectItem>
                        );
                      })}
                    </Select>
                  )}
                />
              </div>
              <div className={styles.select}>
                <Controller<FormValues, typeof TOOL_INFO_FIELDS.serviceType>
                  name={TOOL_INFO_FIELDS.serviceType}
                  rules={VALIDATION_SCHEMA.toolInfo.requestType}
                  control={control}
                  render={({ field: { name, onChange, value }, fieldState: { error } }) => (
                    <Select
                      selectValue={toSelectValue(value, REQUEST_TYPE_OPTIONS)}
                      closeAfterSelection
                      name={name}
                      placeholder="Choose type"
                      label="Service Type *"
                      renderIcon={requestIcon}
                      onChange={onRequestSelectChange(onChange)}
                      error={!!error}
                      errorMessage={error?.message}
                    >
                      {REQUEST_TYPE_OPTIONS.map(({ name, value }) => (
                        <SelectItem key={name} name={name} value={value} className={styles.selectItem}>
                          <div>{value}</div>
                        </SelectItem>
                      ))}
                    </Select>
                  )}
                />
              </div>
            </div>
            <FlexibleFields />
          </>
        )}
        <div className={cn(styles.wrapperInputs, { [styles.BorderPadding]: pageName === PAGE_NAME.REQUEST_QUOTE })}>
          <div className={styles.userUpRowTwo}>
            <FormFieldControlled
              labelTitle="First name*"
              type="text"
              fieldName={INPUT_FIELD_NAMES.firstName}
              className={styles.inputFirstName}
              placeholder="First name"
              rules={VALIDATION_SCHEMA.name}
            />
            <FormFieldControlled
              labelTitle="Last name"
              type="text"
              fieldName={INPUT_FIELD_NAMES.lastName}
              className={styles.inputLastName}
              placeholder="Last name"
            />
          </div>
          <FormFieldControlled
            labelTitle="Email*"
            type="email"
            fieldName={INPUT_FIELD_NAMES.email}
            className={styles.inputEmail}
            placeholder="Email"
            rules={VALIDATION_SCHEMA.email}
          />
          <div className={styles.userUpRowTwo}>
            <FormFieldControlled
              labelTitle="Company name"
              type="text"
              fieldName={INPUT_FIELD_NAMES.companyName}
              className={styles.inputLastName}
              placeholder="Company name"
              rules={VALIDATION_SCHEMA.company}
            />
            <PhoneSelect userCountry={userCountry} className={styles.inputPhone} isCountryLoading={isCountryLoading} />
          </div>
          <FormFieldControlled
            labelTitle="Message*"
            type="text"
            fieldName={INPUT_FIELD_NAMES.message}
            className={styles.inputMessage}
            placeholder="Your message..."
            multiline={true}
            rules={VALIDATION_SCHEMA.message}
          />
          <p className={styles.termsText}>
            By submitting the form, you agree to our{" "}
            <Link href="/tos" className={styles.termsLink} target="_blank" text="Terms of service" /> and{" "}
            <Link href="/privacy" className={styles.termsLink} target="_blank" text="Privacy Policy" />
          </p>
          {errorMessage && <p className={styles.submitError}>{errorMessage}</p>}
          <Button
            disabled={isLoading}
            type="submit"
            text={isLoading ? "Please wait" : "Send"}
            className={styles.submitButton}
          >
            {isLoading && <Loader className={styles.loader} />}
          </Button>
        </div>
      </form>
    </div>
  );
};
